import { type ReactElement, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { guardLeavingPage } from "../navigation/leaveGuard";
import { ConfirmationNotCompleted, useConfirmationModal } from "../useConfirmationModal";

export const useUnsavedInterviewGuard = (
  isUnsaved: boolean,
  leaveWithoutSaving: (keptInOtherTab: boolean) => Promise<void>,
  isOpenElsewhere: () => boolean = () => false,
): ReactElement => {
  const { t } = useTranslation();
  const choice = useConfirmationModal();
  const released = useRef(false);
  const offerToLeave = useRef(() => undefined as void);
  offerToLeave.current = () => {
    const keptInOtherTab = isOpenElsewhere();
    choice.show({
      title: t("Nothing has been saved yet"),
      description: keptInOtherTab
        ? t(
            "«Create with AI» is open with this procurement in another tab, so it is kept there. It is saved as a draft once you confirm the regulations and procedure in that tab.",
          )
        : t(
            "The procurement is saved as a draft once you have confirmed the regulations and procedure. If you leave now, it is not saved.",
          ),
      cancelButtonText: t("Stay") ?? undefined,
      confirmButtonText: (keptInOtherTab ? t("Leave this tab") : t("Leave without saving")) ?? undefined,
      confirmButtonColor: keptInOtherTab ? undefined : "red",
      size: "md",
      onConfirmed: async () => {
        released.current = true;
        try {
          await leaveWithoutSaving(keptInOtherTab);
        } catch {
          released.current = false;
          throw new ConfirmationNotCompleted();
        }
      },
    });
  };

  useEffect(() => {
    if (!isUnsaved) return;
    released.current = false;
    const askBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", askBeforeUnload);
    const stopGuarding = guardLeavingPage(() => {
      if (released.current) return true;
      offerToLeave.current();
      return false;
    });
    return () => {
      window.removeEventListener("beforeunload", askBeforeUnload);
      stopGuarding();
    };
  }, [isUnsaved]);

  return choice.modal;
};
